/* eslint-disable max-lines-per-function */
import { RouteComponentProps, Router } from "@reach/router";
import React, { useEffect } from "react";
import FreshChatWidget from "components/freshchat-widget";
import { authorizeForRole } from "components/auth/authorize-for-role";
import { Roles, Viewer } from "components/auth/types";
import { initializeUser } from "services/analytics";
import { MainLayout } from "pages/Layouts";
import NotFound from "pages/not-found";
import WaitlistUpdate from "pages/waitlist-update/dashboard/components/index";
import WaitlistUpdateContact from "pages/waitlist-update/manual-contact/components/index";
import WaitlistUpdateRemovals from "pages/waitlist-update/process-removals/components/index";
import { Reports, Duplicates } from "pages";
import WaitlistUpdateContainer from "pages/waitlist-update/container";
import Messages from "../messages";

import { ApplicantProfileView } from "../interests-profile/components";
import SettingsPage from "../settings";
import InterestsNew from "../interests-new";
import InterestsByList from "../interests";
import InterestListRedirect from "../lists";
import { SidebarFiltersProvider } from "../interests/sidebar-filters";
import { InterestsTableProvider } from "../interests/interests-table/table-context";

interface Props {
  viewer: Viewer;
}

const ManagerDashboard: React.FC<RouteComponentProps<Props>> = ({ viewer }) => {
  if (!viewer) return null;
  useEffect(() => {
    const { id: userId, email, fullName, roles, organizationId, properties } = viewer;
    const role = roles.indexOf(Roles.Supervisor) > -1 ? "supervisor" : roles[0];
    const { id: propertyId, name: propertyName } = properties[0] || {};
    const onInterestsDashboard = !!location.pathname.match("/dash/manager/lists/");

    // don't initialize if we're curently on the interests dashboard page as this page will
    // call initializeUserLogin on page load, otherwise this will set the property ID to
    // the first available property which can cause incorrect tracking
    if (
      email &&
      fullName &&
      organizationId &&
      userId &&
      propertyId &&
      propertyName &&
      !onInterestsDashboard
    ) {
      initializeUser({
        email,
        fullName,
        organizationId,
        roles: [role],
        userId,
        propertyId,
        propertyName,
      });
    }
  }, []);

  return (
    <MainLayout backgroundColor="gray" showHeader showFooter={false}>
      <FreshChatWidget viewer={viewer} />
      <SidebarFiltersProvider>
        <InterestsTableProvider>
          <Router primary={false} style={{ width: "100%" }}>
            <InterestListRedirect path="lists" viewer={viewer} />
            <InterestsNew path="interests/:listId/new" />
            <ApplicantProfileView path="interest/:interestID/*" viewer={viewer} />
            {["lists/:listId/p/:p/*", "lists/:listId/*"].map(path => <InterestsByList path={path} viewer={viewer} key={path}/>)}

            <WaitlistUpdate path="update" viewer={viewer} />
            <WaitlistUpdateContainer path="update/:list" viewer={viewer}>
              <WaitlistUpdateContact path="contact/*" viewer={viewer} />
              <WaitlistUpdateRemovals path="process/*" viewer={viewer} />
              <WaitlistUpdate path="/*" viewer={viewer} />
            </WaitlistUpdateContainer>

            <Messages path="messages" />

            <Reports path="reports/*" />

            <SettingsPage path="settings/*" viewer={viewer} />

            <Duplicates path="/duplicates/:interestListId/*" viewer={viewer} />

            <NotFound default />
          </Router>
        </InterestsTableProvider>
      </SidebarFiltersProvider>
    </MainLayout>
  );
};

export default authorizeForRole([Roles.PropertyManager], ManagerDashboard);
